import { useState } from "react";
import { cn } from "@/lib/utils";
import { AlertTriangle, Lock, Eye, EyeOff } from "lucide-react";

export function PasswordInput({ className = "", testid, ...props }) {
  const [show, setShow] = useState(false);
  return (
    <div className="relative">
      <input {...props} data-testid={testid} type={show ? "text" : "password"} className={cn(className, "pr-10")} />
      <button type="button" tabIndex={-1} onClick={() => setShow((s) => !s)}
              data-testid={testid ? `${testid}-toggle` : undefined}
              aria-label={show ? "Nascondi password" : "Mostra password"} aria-pressed={show}
              className="absolute inset-y-0 right-0 flex items-center pr-3 text-[var(--muted)] hover:text-[#FC82ED] transition-colors">
        {show ? <EyeOff size={16} /> : <Eye size={16} />}
      </button>
    </div>
  );
}

export const TEMP = {
  calda: { label: "🔥 CALDA", cls: "text-[#FF4D4D] bg-[#FF4D4D]/15 border-[#FF4D4D]/30" },
  media: { label: "🟠 MEDIA", cls: "text-[#FFB020] bg-[#FFB020]/15 border-[#FFB020]/30" },
  fredda: { label: "❄️ FREDDA", cls: "text-[#38BDF8] bg-[#38BDF8]/15 border-[#38BDF8]/30" },
};

const STATO_COLORS = {
  attivo: "text-emerald-400 bg-emerald-400/10 border-emerald-400/30",
  in_trattativa: "text-[#FC82ED] bg-[#FC82ED]/10 border-[#FC82ED]/30",
  venduto: "text-emerald-400 bg-emerald-400/10 border-emerald-400/30",
  bozza: "text-slate-500 bg-slate-400/10 border-slate-400/30",
  documentazione_in_corso: "text-[#FFB020] bg-[#FFB020]/10 border-[#FFB020]/30",
  censito: "text-[#38BDF8] bg-[#38BDF8]/10 border-[#38BDF8]/30",
  pronto_per_pubblicazione: "text-emerald-400 bg-emerald-400/10 border-emerald-400/30",
  scaduto: "text-red-400 bg-red-400/10 border-red-400/30",
  da_pagare: "text-[#FFB020] bg-[#FFB020]/10 border-[#FFB020]/30",
  programmato: "text-[#38BDF8] bg-[#38BDF8]/10 border-[#38BDF8]/30",
  pagato: "text-emerald-400 bg-emerald-400/10 border-emerald-400/30",
  chiusa_positiva: "text-emerald-500 bg-emerald-400/10 border-emerald-400/30",
  chiusa_negativa: "text-red-500 bg-red-400/10 border-red-400/30",
};

export function StatusBadge({ value, testid }) {
  const cls = STATO_COLORS[value] || "text-slate-600 bg-slate-100 border-white/10";
  return (
    <span data-testid={testid} className={cn("inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-semibold uppercase tracking-wide border whitespace-nowrap", cls)}>
      {value === "censito" ? "Da trasformare in incarico" : (value || "").replace(/_/g, " ")}
    </span>
  );
}

export const FASE_VENDITA = {
  proposta_in_corso: ["Proposta in corso", "text-[#B8369F] bg-[#FC82ED]/15 border-[#FC82ED]/40"],
  proposta_subordinata: ["Proposta accettata – subordinata", "text-amber-700 bg-amber-100 border-amber-300"],
  proposta_accettata: ["Proposta accettata", "text-emerald-700 bg-emerald-100 border-emerald-300"],
  preliminare_fissato: ["Preliminare fissato", "text-indigo-700 bg-indigo-100 border-indigo-300"],
  rogitato: ["Rogitato", "text-white bg-[#2B2B2B] border-[#2B2B2B]"],
};

// Stato immobile: la fase di vendita (da proposte/preliminari) prevale sullo stato base.
export function StatoImmobileBadge({ im, testid }) {
  const f = FASE_VENDITA[im?.fase_vendita];
  if (!f) return <StatusBadge value={im?.stato} testid={testid} />;
  return <span data-testid={testid || `fase-${im.fase_vendita}`} className={cn("inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-semibold uppercase tracking-wide border whitespace-nowrap", f[1])}>{f[0]}</span>;
}

export function TempBadge({ value }) {
  const t = TEMP[value] || TEMP.fredda;
  return <span className={cn("inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-bold border", t.cls)}>{t.label}</span>;
}

export function Progress({ value, className }) {
  return (
    <div className={cn("h-2 w-full rounded-full bg-slate-200 overflow-hidden", className)}>
      <div className="h-full rounded-full bg-[#FC82ED] transition-all duration-500" style={{ width: `${Math.min(value, 100)}%` }} />
    </div>
  );
}

export function KpiCard({ label, value, sub, accent, testid, icon: Icon, onClick }) {
  return (
    <div
      data-testid={testid}
      onClick={onClick}
      className={cn(
        "card-immo p-5 flex flex-col gap-2 relative overflow-hidden group",
        onClick && "cursor-pointer hover:-translate-y-0.5"
      )}
    >
      <div className="flex items-center justify-between">
        <span className="text-[11px] font-semibold uppercase tracking-widest text-[var(--muted)]">{label}</span>
        {Icon && <Icon size={18} className={accent ? "text-[#FC82ED]" : "text-[var(--muted)]"} />}
      </div>
      <div className={cn("font-display text-3xl font-extrabold mono", accent ? "text-[#FC82ED]" : "text-[#111111]")}>{value}</div>
      {sub && <div className="text-xs text-[var(--muted)]">{sub}</div>}
    </div>
  );
}

export function DuplicateWarning({ dup }) {
  if (!dup) return null;
  const other = !dup.same_owner;
  const Icon = other ? Lock : AlertTriangle;
  return (
    <div data-testid="dup-warning" className={cn(
      "rounded-xl border p-3 flex items-start gap-2.5",
      other ? "border-[#FFB020]/40 bg-[#FFB020]/10" : "border-red-400/40 bg-red-400/10"
    )}>
      <Icon size={18} className={cn("shrink-0 mt-0.5", other ? "text-[#FFB020]" : "text-red-500")} />
      <div>
        <div className={cn("font-bold uppercase tracking-wide text-[11px] mb-0.5", other ? "text-[#8a5b00]" : "text-red-600")}>
          Contatto già presente nel CRM
        </div>
        <p className="text-sm text-slate-600">
          {other
            ? "Questo recapito è già gestito da un altro agente. Per la policy sulla privacy i dati del contatto non sono visibili. Rivolgiti alla Direzione per la gestione."
            : "Hai già questo contatto nella tua lista. Evita di crearne un duplicato: gestisci la scheda esistente."}
        </p>
      </div>
    </div>
  );
}

export function SectionTitle({ children, sub }) {
  return (
    <div className="mb-5">
      <h2 className="font-display text-xl lg:text-2xl font-bold tracking-tight">{children}</h2>
      {sub && <p className="text-sm text-[var(--muted)] mt-1">{sub}</p>}
    </div>
  );
}
